import {setTimeout} from 'timers';

import {Signal} from 'openfin-service-signal';
import {Identity} from 'openfin/_v2/main';

import {whenFin} from '../utils/whenFin';

import {Theme, ThemeCollection} from './types';
import {defaultTheme} from './themes/default';
import {lightTheme} from './themes/light';

type ThemeChangeHandler = (theme: Theme) => void;

enum StorageKeys {
    CURRENT = 'themes@current',
    ALL = 'themes@all'
}

enum Topics {
    THEME = 'OFD_THEME',
    REQUEST = 'OFD_REQUEST_THEME'
}

interface ThemeTimestamped {
    timestamp: number;
    theme: Theme;
}

// We pass in the API calls so we can use the same backend that the app is using
interface ThemeAPI {
    getAllThemes?: () => Promise<ThemeCollection>;
    getCurrentThemeId?: () => Promise<string | undefined>;
    setCurrentThemeId?: (id: string) => Promise<void>;
}

/**
 * Download all themes and the current user theme.
 * This function only needs to be invoked by apps that can set themes.
 * @param api API calls to download and set themes.
 */
export async function initializeThemeSelector(api: ThemeAPI): Promise<void> {
    themeAPI = api;
    // If this application is capable of changing themes it can download themes
    // and can check the cloud for the current set theme.
    try {
        await downloadThemes();
    } catch (error) {
        console.error('Themes download failed', error);
        throw error;
    }
}

/**
 * Get the current theme.
 */
export function getCurrentTheme(): ThemeTimestamped | null {
    const themeStr = localStorage.getItem(StorageKeys.CURRENT);
    if (!themeStr) {
        return null;
    }
    return JSON.parse(themeStr);
}

/**
 * Get all themes.
 */
export function getAllThemes(): ThemeCollection {
    const themesStr = localStorage.getItem(StorageKeys.ALL);
    if (!themesStr) {
        return {};
    }
    return JSON.parse(themesStr);
}

/**
 * Set the current theme and broadcast it to all applications.
 * @param theme Theme to save.
 * @param cloudSave If true cloud settings will be updated with the new theme id.
 */
export async function setTheme(theme: Theme, cloudSave = false): Promise<void> {
    // Only apps that can select themes can set timestamps. This lets us apply
    // the most recent theme the user selected, no matter what order apps open
    // or if there is stale data.
    const timestampedTheme: ThemeTimestamped = {
        timestamp: Date.now(),
        theme
    };
    whenFin(() => fin.InterApplicationBus.publish(Topics.THEME, timestampedTheme), () => setThemeSignal.emit(theme));
    saveCurrentTheme(timestampedTheme);
    if (cloudSave && themeAPI?.setCurrentThemeId) {
        await themeAPI?.setCurrentThemeId(theme.id);
        console.log(`Saved theme ${theme.id} to the cloud`);
    }
}

/**
 * Subscribe to theme changes.
 * @param callback A callback function that is invoked when a theme is changed.
 * @returns Unsubscribe from theme updates
 */
export async function addThemeListener(callback: ThemeChangeHandler): Promise<() => void> {
    const unsubscribe = setThemeSignal.add(callback).remove;
    // Trigger immediately if delay between subcription and initialization
    if (lastTheme && callback) {
        callback(lastTheme.theme);
    }

    return unsubscribe;
}

// Map of HTTP API requests for cloud
let themeAPI: ThemeAPI | undefined;
let lastTheme: ThemeTimestamped | undefined;
const setThemeSignal = new Signal<[Theme]>();

/**
 * Default themes built into desktop-ui.
 */
function defaultThemes(): ThemeCollection {
    return {defaultTheme, lightTheme};
}

// Load theming api on window for testing
Object.assign(window, {
    theming: {
        requestCurrentTheme,
        getCurrentTheme,
        getAllThemes,
        setTheme,
        themes: defaultThemes()
    }
});

// Auto initialize theme communication
(async () => {
    if (typeof fin === 'undefined') {
        return;
    }

    const listener = async (payload: ThemeTimestamped, sender: Identity) => {
        if (!payload || !sender) {
            return;
        }
        const currentThemeTimestamp = getCurrentTheme()?.timestamp ?? 0;
        const {theme, timestamp} = payload;
        // @ts-ignore fin.me
        const myUuid = fin.me.identity.uuid;

        // Only allow themes that are more recent or set by this app
        if (theme && (timestamp > currentThemeTimestamp || sender.uuid === myUuid)) {
            console.log(`${document.title} Received theme update from:`, sender, payload);
            lastTheme = payload;
            saveCurrentTheme(payload);
            setThemeSignal.emit(payload.theme);

            // TODO: [SERVICE-1169] There is a bug where child windows will not receive IAB messages
            // from other apps if the parent has a listener on the same topic.
            // To fix this main window will ferry the payload to child windows as
            // if it had sent the theme update.
            if (sender.uuid !== myUuid && fin.Window.getCurrentSync().isMainWindow()) {
                const children = await fin.Application.getCurrentSync().getChildWindows();
                children.forEach((child) => {
                    fin.InterApplicationBus.send(child.identity, Topics.THEME, payload);
                });
            }
        }
    };

    await whenFin(async () => {
        // THEME topic is noisy so only take the most recent timestamped theme
        await fin.InterApplicationBus.subscribe({uuid: '*'}, Topics.THEME, debounceBestMatch(listener, timestampSelector, 100));
        await requestCurrentTheme();
        await addCurrentThemeResponder();
    });
    console.log('Theme subscriptions setup complete');
})();

/**
 * Listen for requests from apps for the current theme.
 */
function addCurrentThemeResponder(): Promise<void> {
    return fin.InterApplicationBus.subscribe({uuid: '*'}, Topics.REQUEST, (_: any, sender: Identity) => {
        // @ts-ignore fin.me
        if (sender.uuid !== fin.me.identity.uuid) {
            console.log('Received request for current theme from:', sender);
            const theme = getCurrentTheme();
            if (theme) {
                fin.InterApplicationBus.send(sender, Topics.THEME, theme);
            }
        }
    });
}

/**
 * Save the current theme locally
 * @param theme Theme object to save.
 */
async function saveCurrentTheme(theme: ThemeTimestamped): Promise<void> {
    localStorage.setItem(StorageKeys.CURRENT, JSON.stringify(theme));
}

/**
 * Request the current theme from another application.
 * Apps that are set up to respond will reply on the THEME topic.
 */
async function requestCurrentTheme(): Promise<void> {
    console.log('Requesting current theme...');
    await fin.InterApplicationBus.publish(Topics.REQUEST, null);
}

async function downloadThemes(): Promise<void> {
    let allThemes: ThemeCollection = {};
    let currentThemeId: string | undefined;

    if (themeAPI?.getAllThemes) {
        allThemes = await themeAPI.getAllThemes().catch((error) => {
            console.error(
                'Unable to download all themes, using default themes',
                error
            );
            return defaultThemes();
        });
    } else {
        allThemes = defaultThemes();
    }

    localStorage.setItem(StorageKeys.ALL, JSON.stringify(allThemes));

    if (themeAPI?.getCurrentThemeId) {
        currentThemeId = await themeAPI.getCurrentThemeId().catch((error) => {
            console.error('Unable to get the current theme id from the cloud', error);
            return undefined;
        });
    }

    if (currentThemeId && allThemes[currentThemeId]) {
        localStorage.setItem(
            StorageKeys.CURRENT,
            JSON.stringify(allThemes[currentThemeId])
        );
        setTheme(allThemes[currentThemeId]);
    }
}

/**
 * Debounce a function within the delay timespan.
 * When the function is eventually invoked, it will use the selector function
 * to filter and select the best arguments to use.
 */
function debounceBestMatch<A extends unknown[]>(
    func: (...args: A) => void,
    selector: (previous: A[]) => A,
    delay: number
): (...args: A) => void {
    let inDebounce: NodeJS.Timeout;
    let previousArgs: A[] = [];

    const finalFunc = () => {
        const selectedArgs = selector(previousArgs);
        previousArgs = [];
        func(...selectedArgs);
    };

    return (...args: A) => {
        previousArgs.push(args);
        clearTimeout(inDebounce);
        inDebounce = setTimeout(finalFunc, delay);
    };
}

/**
 * Select the most recent timestamp in an array of theme messages.
 */
function timestampSelector(themeMessages: [ThemeTimestamped, Identity][]): [ThemeTimestamped, Identity] {
    return themeMessages.sort(([a], [b]) => a.timestamp - b.timestamp)?.[themeMessages.length - 1];
}
